import {
  Button,
  Grid,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  InputAdornment,
  IconButton,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  Popover,
  Dialog,
  Paper,
  CircularProgress,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
  MenuItem,
} from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import CheckOverflow from "../../utils/CheckOverflow";
import TaskSheetReportAndResolve from "../TaskSheetReportAndResolve";
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "react-query";
import {
  decideSolution,
  getApprovalIssueData,
  getIssueDetails,
  getReportIssueData,
} from "./mutation";
import dayjs from "dayjs";
import Loader from "../../components/Loader";
import { getUsers } from "../Blowroom/mutation/auth/auth";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { handleToastMsg } from "../../components/Toast";
import { HALL_NO } from "../../config/masterdata";
import { solutionImages } from "../../utils/solutionImages";
import { STATUS_COLORS, statusStyle } from "../../utils/issueStatus";
import { acquireSocket, releaseSocket } from "../../utils/socket";
import {
  formatReportedAt,
  formatReportedDate,
  formatReportedTime,
} from "../../utils/taskSheetDate";

// Sent by the server whenever a task sheet issue is created or saved
const ISSUE_CHANGED_EVENT = "reportIssue:changed";

// A solution's most recent decline (remarks, date, by), if any
const lastDeclineOf = (solution) =>
  (solution?.reviews || [])
    .filter((review) => review?.decision === "decline")
    .slice(-1)[0];

// Times a solution on the issue was declined, and when last ("-" if never)
const rejectedCountText = (issue) =>
  issue?.rejectedCount > 0 ? issue.rejectedCount : "-";
const lastRejectedText = (issue) =>
  issue?.lastRejectedAt
    ? dayjs(issue.lastRejectedAt).format("DD-MM-YYYY, hh:mm A")
    : "-";

function TaskSheet({ isApproval = false }) {
  // Approver view (Task Sheet Approvals page): superiors of the reporting
  // managers accept or decline their solutions
  const isERP = isApproval;
  // Approvals are per user; keyed by id so another login never sees them
  const APPROVALS_KEY = ["task-sheets-approvals", localStorage.getItem("id")];
  // Approver rows load in chunks of this many table pages per request
  const PAGES_PER_FETCH = 5;

  const queryClient = useQueryClient();

  const { mutate, isLoading: isERPmutateLoading } =
    useMutation(decideSolution);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [filterSlNo, setFilterSlNo] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [isForm, setIsform] = useState(false);
  const [isRaiseReport, setIsRaiseReport] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState(false);

  //this is for pretend the rerender issue when we give checkoverflow params
  const [filteredData, setFilteredData] = useState([]);
  const [erpSolution, setErpSolution] = useState("withSolution");
  const [hallNo, setHallNo] = useState("");

  // Employee / sub manager view: all of their own issues in one request
  const {
    data: reportIssueData,
    isLoading: isIssuesLoading,
    refetch,
  } = useQuery(
    "task-sheets-report-issue",
    async () => {
      if (!isRaiseReport) {
        setIsLoadingData(true);
      }

      try {
        return await getReportIssueData();
      } finally {
        if (!isRaiseReport) {
          setIsLoadingData(false);
        }
      }
    },
    {
      enabled: !isERP,
      refetchOnMount: true,
      // refetchInterval: 5000, // Poll every 5 seconds (5000ms)
    }
  );

  // Approver view: rows load in chunks of PAGES_PER_FETCH table pages; each
  // chunk continues after the last loaded row (keyset paging on the server).
  // Loaded rows stay in memory and cache, so Back / rows-per-page changes
  // never call the server again.
  const approvalFilters = {
    slNo: filterSlNo || undefined,
    hall: hallNo || undefined,
    solution: erpSolution,
  };
  const approvalsQueryKey = [...APPROVALS_KEY, approvalFilters];
  const {
    data: approvalData,
    isLoading: isApprovalsLoading,
    hasNextPage: hasMoreApprovals,
    fetchNextPage: fetchMoreApprovals,
    isFetchingNextPage: isFetchingMoreApprovals,
  } = useInfiniteQuery(
    approvalsQueryKey,
    ({ pageParam }) =>
      getApprovalIssueData({
        ...approvalFilters,
        pageSize: rowsPerPage * PAGES_PER_FETCH,
        ...pageParam,
      }),
    {
      enabled: isERP,
      getNextPageParam: (lastChunk) => {
        const last = lastChunk?.data?.[lastChunk.data.length - 1];
        return lastChunk?.hasNext && last
          ? { direction: "next", cursorKey: last.sortKey, cursorSlNo: last.slNo }
          : undefined;
      },
      // Re-fetched only after 5 minutes away or on a filter change; no reload
      // when the tab regains focus
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
      keepPreviousData: true,
    }
  );
  const approvalRows = approvalData?.pages?.flatMap((chunk) => chunk.data) ?? [];
  // Live total as of the latest chunk
  const approvalTotal =
    approvalData?.pages?.[approvalData.pages.length - 1]?.total ?? 0;

  const isReportDataLoading = isERP ? isApprovalsLoading : isIssuesLoading;

  //this is for make fresh api call when component render and it will avoid the cache
  useEffect(() => {
    if (!isERP) refetch();
  }, [refetch, isERP]);

  // Managers for the "Report an issue" form (not needed by approvers)
  const { data: users, isLoading } = useQuery("users", getUsers, {
    enabled: !isERP,
    refetchOnMount: true,
  });

  //this is for filter the manager users only
  const filteredManagers =
    users?.filter((item) => item.role === "MANAGER") || [];

  // Approver: when the next table page isn't fully loaded yet, load the next
  // chunk in the background so Next stays instant
  useEffect(() => {
    if (!isERP || !hasMoreApprovals || isFetchingMoreApprovals) return;
    if ((page + 2) * rowsPerPage > approvalRows.length) {
      fetchMoreApprovals();
    }
  }, [
    isERP,
    page,
    rowsPerPage,
    approvalRows.length,
    hasMoreApprovals,
    isFetchingMoreApprovals,
    fetchMoreApprovals,
  ]);

  // A decision can empty the last page: step back to the last page with rows
  useEffect(() => {
    if (!isERP || page === 0 || hasMoreApprovals) return;
    if (page * rowsPerPage >= approvalRows.length) {
      setPage(Math.max(Math.ceil(approvalRows.length / rowsPerPage) - 1, 0));
    }
  }, [isERP, page, rowsPerPage, approvalRows.length, hasMoreApprovals]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleFilterChange = (event) => {
    if (isNaN(event.target.value)) {
      alert("Please enter a number only");
      return;
    }

    setFilterSlNo(event.target.value);
    setPage(0);
  };

  const handleResetFilter = () => {
    setFilterSlNo("");
    setPage(0);
  };

  const handleStatusFilterChange = (event, newStatus) => {
    if (newStatus !== null) {
      setStatusFilter(newStatus);
      setPage(0);
    }
  };

  const handleOptionChange = (e) => {
    setErpSolution(e.target.value);
    setPage(0);
  };

  const handleHallChange = (e) => {
    setHallNo(parseInt(e.target.value));
    setPage(0);
  };

  //this is for showing the filtered data based on conditions
  useEffect(() => {
    if (!isERP) {
      const newFilteredData =
        reportIssueData?.data?.filter((row) => {
          const matchesFilterTerm =
            !filterSlNo || row.slNo.toString() === filterSlNo;
          const matchesStatusFilter =
            statusFilter === "All" || row.status === statusFilter;

          return matchesFilterTerm && matchesStatusFilter;
        }) || [];

      setFilteredData(newFilteredData);
    }
  }, [filterSlNo, reportIssueData?.data, statusFilter, isERP]);

  // Approver: the rows of the current table page, from the loaded chunks
  const approvalPageRows = approvalRows.slice(
    page * rowsPerPage,
    (page + 1) * rowsPerPage
  );
  useEffect(() => {
    if (isERP) setFilteredData(approvalPageRows);
    // approvalPageRows is derived from these
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isERP, approvalData, page, rowsPerPage]);

  // After a decision: update the loaded rows in place instead of re-fetching
  // every chunk; an issue whose status no longer matches the tab (resolved,
  // rejected, or accepted out of Rejected) leaves the list
  const updateLoadedApproval = (issue) => {
    const tabStatus = erpSolution === "rejected" ? "REJECTED" : "PENDING";
    queryClient.setQueryData(approvalsQueryKey, (old) => {
      if (!old?.pages) return old;
      const isLoaded = old.pages.some((chunk) =>
        chunk.data.some((row) => row.id === issue.id)
      );
      const leaves = isLoaded && issue?.status !== tabStatus;
      return {
        ...old,
        pages: old.pages.map((chunk) => ({
          ...chunk,
          data: leaves
            ? chunk.data.filter((row) => row.id !== issue.id)
            : chunk.data.map((row) =>
                row.id === issue.id
                  ? {
                      ...row,
                      status: issue.status,
                      rejectedCount: issue.rejectedCount,
                      lastRejectedAt: issue.lastRejectedAt,
                    }
                  : row
              ),
          total: leaves ? Math.max(chunk.total - 1, 0) : chunk.total,
        })),
      };
    });
  };

  // Full ticket (images, solutions) for the approver dialog, cached so a
  // second view of the same ticket opens instantly
  const issueDetailsKey = (id) => ["task-sheet-issue", id];
  const fetchIssueDetails = (id) =>
    queryClient.fetchQuery(issueDetailsKey(id), () => getIssueDetails(id), {
      staleTime: 5 * 60 * 1000,
    });
  // Start loading as soon as the pointer is over the eye icon
  const prefetchIssueDetails = (id) => {
    if (isERP) fetchIssueDetails(id).catch(() => {});
  };

  //handle overflow text
  const [isOverflowDraft, addDraftRef] = CheckOverflow(filteredData);

  const [anchorEl, setAnchorEl] = useState(null);
  const [popoverContent, setPopoverContent] = useState("");

  const handlePopoverOpen = (event, id, content) => {
    if (isOverflowDraft[id]) {
      setAnchorEl(event.currentTarget);
      setPopoverContent(content);
    }
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
    setPopoverContent("");
  };

  const open = Boolean(anchorEl);

  const handleReport = () => {
    setIsform(true);
    setIsRaiseReport(true);
  };

  const goBack = () => {
    setIsform(false);
    setIsRaiseReport(false);
  };

  /**handle view the employee/sub manager's issued data */
  const [openDialog, setOpenDialog] = useState(false);
  const [showIssuedData, setShowIssuedData] = useState({});
  // Seek reason column only when a manager actually gave one
  const hasSeekReason = Boolean(
    showIssuedData?.reportingManagerSolution?.some((item) => item?.seekReason)
  );

  const [openDialogERP, setOpenDialogERP] = useState(false);
  const [loadingViewId, setLoadingViewId] = useState(null);
  const handleViewDetails = async (rowData) => {
    if (isERP) {
      // The approvals list is slim; load images and solutions on demand
      setLoadingViewId(rowData.id);
      try {
        const details = await fetchIssueDetails(rowData.id);
        setShowIssuedData({
          ...details,
          approvableManagerIds: rowData.approvableManagerIds,
        });
        setOpenDialogERP(true);
      } catch (error) {
        handleToastMsg("Unable to load the task sheet", "error");
      } finally {
        setLoadingViewId(null);
      }
    } else {
      setOpenDialog(true);
      setShowIssuedData(rowData);
    }
  };

  const handleClose = () => {
    setOpenDialog(false);
    setOpenDialogERP(false);
    setShowIssuedData({});
  };

  // Live updates without a reload: when a manager resubmits (or anyone saves
  // a ticket), refresh the approvals list and, if that ticket is open here,
  // reload it so its new solution and Accept / Decline show up
  const openIssueIdRef = useRef(null);
  useEffect(() => {
    openIssueIdRef.current = openDialogERP ? showIssuedData?.id : null;
  }, [openDialogERP, showIssuedData?.id]);

  useEffect(() => {
    if (!isERP) return undefined;

    let listTimer = null;
    const onIssueChanged = ({ id } = {}) => {
      if (!id) return;
      // Cached details of this ticket are out of date now
      queryClient.removeQueries(issueDetailsKey(id));

      if (openIssueIdRef.current === id) {
        fetchIssueDetails(id)
          .then((details) =>
            setShowIssuedData((current) =>
              current?.id === id
                ? {
                    ...details,
                    approvableManagerIds: current.approvableManagerIds,
                  }
                : current
            )
          )
          .catch(() => {});
      }

      // Several saves in a row: one list refresh
      clearTimeout(listTimer);
      listTimer = setTimeout(
        () => queryClient.invalidateQueries(APPROVALS_KEY),
        1000
      );
    };

    const socket = acquireSocket();
    socket.on(ISSUE_CHANGED_EVENT, onIssueChanged);
    return () => {
      clearTimeout(listTimer);
      socket.off(ISSUE_CHANGED_EVENT, onIssueChanged);
      releaseSocket();
    };
    // queryClient and the key helpers don't change while mounted
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isERP]);

  //handle the image show in the dialog box
  const [openNew, setOpenNew] = useState(false);
  const [selectedImage, setSelectedImage] = useState("");

  const handleClickOpen = (image) => {
    setSelectedImage(image);
    setOpenNew(true);
  };

  const handleCloseImageDialog = () => {
    setOpenNew(false);
  };

  // Accept / Decline open the remarks dialog first; remarks are required
  const [reviewTarget, setReviewTarget] = useState(null); // { key, id, name }
  const [reviewRemarks, setReviewRemarks] = useState("");
  const openReview = (key, managerSolution) => {
    setReviewTarget({
      key,
      id: managerSolution?.id,
      name: managerSolution?.name,
    });
    setReviewRemarks("");
  };
  const closeReview = () => {
    if (isERPmutateLoading) return;
    setReviewTarget(null);
  };

  const handleAcceptDecline = (key, id, remarks) => {
    // The server checks that the user is this manager's superior
    const params = {
      id: showIssuedData?.id,
      managerId: id,
      decision: key === "accepted" ? "accept" : "decline",
      remarks,
    };

    mutate(params, {
      onSuccess: (res) => {
        handleToastMsg("Data updated successfully", "success");
        setReviewTarget(null);
        // Show the decision in the open dialog straight away
        setShowIssuedData((current) => ({
          ...current,
          ...res?.data,
          approvableManagerIds: current?.approvableManagerIds,
        }));
        // Keep the cached ticket and the loaded rows in step without
        // re-fetching; lists cached for other filters refresh when reopened
        if (res?.data?.id) {
          queryClient.setQueryData(issueDetailsKey(res.data.id), res.data);
          updateLoadedApproval(res.data);
        }
        queryClient.invalidateQueries(APPROVALS_KEY, { refetchActive: false });
      },
      onError: (error) => {
        const message = error?.response?.data?.message;
        // Resolved by someone else meanwhile: drop it from the list
        if (message === "Issue is already resolved") {
          updateLoadedApproval({ id: showIssuedData?.id, status: "RESOLVED" });
        }
        handleToastMsg(message || "Data Error Occurred", "error");
      },
    });
  };

  if (isReportDataLoading || isLoading || isLoadingData) {
    return <Loader />;
  }

  return (
    <Grid
      container
      item
      xs={12}
      sx={{
        padding: "36px",
        "@media (max-width: 600px)": {
          width: "100%",
          marginLeft: "0",
          marginTop: "20px",
        },
      }}
      alignContent={"flex-start"}
      rowGap={4}
    >
      {isForm ? (
        <Grid container item xs={12}>
          {" "}
          <TaskSheetReportAndResolve
            goBack={goBack}
            isRaiseReport={isRaiseReport}
            filteredManagers={filteredManagers}
          />
        </Grid>
      ) : (
        <Grid container item xs={12} rowGap={4}>
          <Grid
            container
            item
            xs={12}
            alignItems={"center"}
            justifyContent={"space-between"}
          >
            <Grid item sm={6} xs={9}>
              <TextField
                fullWidth
                placeholder="Enter Sl No to filter"
                value={filterSlNo}
                onChange={handleFilterChange}
                InputProps={{
                  endAdornment: filterSlNo && (
                    <InputAdornment position="end">
                      <IconButton onClick={handleResetFilter}>
                        <CloseIcon />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            {isERP && (
              <Grid
                container
                item
                md={12}
                sm={12}
                xs={9}
                mt={3}
                alignItems="center"
                spacing={2}
              >
                <Grid item md={6} sm={12} xs={12}>
                  <FormControl>
                    <RadioGroup
                      aria-labelledby="demo-radio-buttons-group-label"
                      value={erpSolution}
                      name="radio-buttons-group"
                      onChange={handleOptionChange}
                      row
                    >
                      <FormControlLabel
                        value="withSolution"
                        control={<Radio />}
                        label="With Solution"
                      />
                      <FormControlLabel
                        value="withOutSolution"
                        control={<Radio />}
                        label="Without Solution"
                      />
                      {/* Declined, waiting for the manager to resubmit */}
                      <FormControlLabel
                        value="rejected"
                        control={<Radio />}
                        label="Rejected"
                      />
                    </RadioGroup>
                  </FormControl>
                </Grid>

                <Grid item md={6} sm={6} xs={12}>
                  <TextField
                    value={hallNo}
                    select
                    onChange={handleHallChange}
                    type="number"
                    sx={{ width: 200 }}
                    id="outlined-basic"
                    label="Hall Number"
                    variant="outlined"
                    // onInput={handleCardingLineNumInput}
                  >
                    {HALL_NO?.map((hall) => (
                      <MenuItem key={hall} value={hall}>
                        {hall}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
              </Grid>
            )}

            {!isERP && (
              <Grid item>
                <Button
                  variant="contained"
                  color="primary"
                  // fullWidth
                  sx={{
                    mt: { md: 0, sm: 0, xs: 2 },
                    fontSize: { md: "18px", xs: "14" },
                  }}
                  style={{ textTransform: "none" }}
                  onClick={handleReport}
                >
                  Report an issue
                </Button>
              </Grid>
            )}
          </Grid>

          {!isERP && (
            <Grid
              container
              item
              md={6}
              sm={6}
              xs={8}
              style={{ overflowX: "auto" }}
            >
              <ToggleButtonGroup
                value={statusFilter}
                exclusive
                onChange={handleStatusFilterChange}
                aria-label="status filter"
                s
                fullWidth
              >
                <ToggleButton value="All" aria-label="all">
                  All
                </ToggleButton>
                <ToggleButton value="PENDING" aria-label="pending">
                  Pending
                </ToggleButton>
                <ToggleButton value="REJECTED" aria-label="rejected">
                  Rejected
                </ToggleButton>
                <ToggleButton value="RESOLVED" aria-label="resolved">
                  Resolved
                </ToggleButton>
              </ToggleButtonGroup>
            </Grid>
          )}

          <Grid container item md={12} sm={12} xs={9}>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow style={{ backgroundColor: "#f5f5f5" }}>
                    <TableCell style={{ maxWidth: "80px", fontWeight: "600" }}>
                      SL NO
                    </TableCell>
                    <TableCell style={{ maxWidth: "80px", fontWeight: "600" }}>
                      DATE
                    </TableCell>
                    <TableCell style={{ maxWidth: "100px", fontWeight: "600" }}>
                      DESCRIPTION
                    </TableCell>
                    <TableCell style={{ maxWidth: "100px", fontWeight: "600" }}>
                      DEPARTMENT
                    </TableCell>
                    <TableCell style={{ maxWidth: "100px", fontWeight: "600" }}>
                      REPORTING MANAGER
                    </TableCell>
                    {isERP && (
                      <>
                        <TableCell
                          style={{ maxWidth: "80px", fontWeight: "600" }}
                        >
                          REJECTED COUNT
                        </TableCell>
                        <TableCell
                          style={{ maxWidth: "100px", fontWeight: "600" }}
                        >
                          LAST REJECTED
                        </TableCell>
                      </>
                    )}
                    {!isERP && (
                      <TableCell
                        style={{ maxWidth: "100px", fontWeight: "600" }}
                      >
                        ACTION / STATUS
                      </TableCell>
                    )}

                    <TableCell style={{ maxWidth: "20px", fontWeight: "600" }}>
                      VIEW
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredData?.length > 0 ? (
                    (isERP
                      ? filteredData // one page, already ordered by the server
                      : filteredData
                          ?.sort((a, b) => new Date(b.date) - new Date(a.date))
                          ?.slice(
                            page * rowsPerPage,
                            page * rowsPerPage + rowsPerPage
                          )
                    )?.map((row) => (
                        <TableRow key={row.id}>
                          <TableCell style={{ maxWidth: "80px" }}>
                            <p className="overflowContain"> {row.slNo}</p>
                          </TableCell>
                          <TableCell style={{ maxWidth: "80px" }}>
                            <p className="overflowContain">
                              {" "}
                              {formatReportedDate(row)}
                              <br />
                              {formatReportedTime(row)}
                            </p>
                          </TableCell>
                          <TableCell style={{ maxWidth: "150px", wordBreak: "break-word", overflowWrap: "break-word" }}>
                            <p
                              ref={addDraftRef(`description-${row.id}`)}
                              onMouseEnter={(e) =>
                                handlePopoverOpen(
                                  e,
                                  `description-${row.id}`,
                                  row.description
                                )
                              }
                              onMouseLeave={handlePopoverClose}
                              style={{
                                color: "#e03131",
                                fontWeight: "bold",
                                textAlign: "justify",
                                wordBreak: "break-word",
                                overflowWrap: "break-word",
                                whiteSpace: "normal",
                                margin: 0,
                              }}
                            >
                              {row.description}
                            </p>
                          </TableCell>
                          <TableCell style={{ maxWidth: "100px" }}>
                            <p
                              className="overflowContain"
                              ref={addDraftRef(`department-${row.id}`)}
                              onMouseEnter={(e) =>
                                handlePopoverOpen(
                                  e,
                                  `department-${row.id}`,
                                  row?.department
                                    ?.map((item) => item)
                                    .join(", ")
                                )
                              }
                              onMouseLeave={handlePopoverClose}
                            >
                              {row?.department?.map((item) => item).join(", ")}
                            </p>
                          </TableCell>
                          <TableCell style={{ maxWidth: "100px" }}>
                            <p
                              className="overflowContain"
                              ref={addDraftRef(`reportingManager-${row.id}`)}
                              onMouseEnter={(e) =>
                                handlePopoverOpen(
                                  e,
                                  `reportingManager-${row.id}`,
                                  row?.reportingManager
                                    ?.map((item) => item?.name)
                                    .join(", ")
                                )
                              }
                              onMouseLeave={handlePopoverClose}
                            >
                              {row?.reportingManager
                                ?.map((item) => item?.name)
                                .join(", ")}
                            </p>
                          </TableCell>

                          {isERP && (
                            <>
                              <TableCell
                                style={{
                                  maxWidth: "80px",
                                  color:
                                    row.rejectedCount > 0
                                      ? STATUS_COLORS.REJECTED
                                      : undefined,
                                  fontWeight: row.rejectedCount > 0 ? 600 : 400,
                                }}
                              >
                                {rejectedCountText(row)}
                              </TableCell>
                              <TableCell style={{ maxWidth: "100px" }}>
                                {lastRejectedText(row)}
                              </TableCell>
                            </>
                          )}

                          {!isERP && (
                            <TableCell style={{ maxWidth: "100px" }}>
                              <p
                                className="overflowContain"
                                ref={addDraftRef(`status-${row.id}`)}
                                onMouseEnter={(e) =>
                                  handlePopoverOpen(
                                    e,
                                    `status-${row.id}`,
                                    row.status
                                  )
                                }
                                onMouseLeave={handlePopoverClose}
                                style={{ color: STATUS_COLORS[row.status] }}
                              >
                                <span style={statusStyle(row.status)}>{row.status}</span>
                              </p>
                            </TableCell>
                          )}

                          <TableCell sx={{ maxWidth: "20px" }}>
                            {loadingViewId === row.id ? (
                              <CircularProgress size={26} />
                            ) : (
                            <VisibilityIcon
                              sx={{
                                fontSize: "30px",
                                color: "#00897B",
                                cursor: "pointer",
                              }}
                              onClick={() => handleViewDetails(row)}
                              onMouseEnter={() => prefetchIssueDetails(row.id)}
                            />
                            )}
                          </TableCell>
                        </TableRow>
                      ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={10}>
                        <Typography align="center">
                          {isERP && isFetchingMoreApprovals ? (
                            <CircularProgress size={26} />
                          ) : (
                            "No data available"
                          )}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>

              <Popover
                sx={{
                  pointerEvents: "none",
                }}
                open={open}
                anchorEl={anchorEl}
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "left",
                }}
                transformOrigin={{
                  vertical: "top",
                  horizontal: "left",
                }}
                onClose={handlePopoverClose}
                disableRestoreFocus
              >
                <p style={{ padding: "15px" }}>{popoverContent}</p>
              </Popover>
            </TableContainer>

            {isERP ? (
              // Pages sliced from the loaded chunks; live total from the
              // server; Next stays on while more rows are loaded or loadable
              <TablePagination
                rowsPerPageOptions={[5, 10, 15]}
                component="div"
                count={approvalTotal}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                labelDisplayedRows={() =>
                  approvalTotal === 0 || !filteredData.length
                    ? `0 of ${approvalTotal}`
                    : `${page * rowsPerPage + 1}–${
                        page * rowsPerPage + filteredData.length
                      } of ${approvalTotal}`
                }
                backIconButtonProps={{ disabled: page === 0 }}
                nextIconButtonProps={{
                  disabled: !(
                    (page + 1) * rowsPerPage < approvalRows.length ||
                    hasMoreApprovals
                  ),
                }}
              />
            ) : (
              <TablePagination
                rowsPerPageOptions={[5, 10, 15]}
                component="div"
                count={filteredData?.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            )}
          </Grid>

          {/* Remarks for an accept / decline (required) */}
          <Dialog
            open={Boolean(reviewTarget)}
            onClose={closeReview}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle>
              {reviewTarget?.key === "accepted" ? "Accept" : "Decline"}{" "}
              solution{reviewTarget?.name ? ` of ${reviewTarget.name}` : ""}
            </DialogTitle>
            <DialogContent>
              <TextField
                autoFocus
                multiline
                rows={4}
                fullWidth
                required
                label="Remarks"
                value={reviewRemarks}
                onChange={(e) => setReviewRemarks(e.target.value)}
                inputProps={{ maxLength: 1000 }}
                style={{ marginTop: "8px" }}
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={closeReview} disabled={isERPmutateLoading}>
                Cancel
              </Button>
              <Button
                variant="contained"
                style={{
                  backgroundColor: !reviewRemarks.trim()
                    ? undefined
                    : reviewTarget?.key === "accepted"
                    ? "#2f9e44"
                    : "#e03131",
                  color: "white",
                  textTransform: "none",
                }}
                disabled={!reviewRemarks.trim() || isERPmutateLoading}
                onClick={() =>
                  handleAcceptDecline(
                    reviewTarget.key,
                    reviewTarget.id,
                    reviewRemarks.trim()
                  )
                }
              >
                {isERPmutateLoading ? (
                  <CircularProgress size={24} sx={{ color: "#FFF" }} />
                ) : reviewTarget?.key === "accepted" ? (
                  "Accept"
                ) : (
                  "Decline"
                )}
              </Button>
            </DialogActions>
          </Dialog>

          {/**this dialog box for showing the image to the manager */}
          <Dialog open={openNew} onClose={handleCloseImageDialog} maxWidth="lg">
            <DialogTitle>
              <IconButton
                aria-label="close"
                onClick={handleCloseImageDialog}
                style={{ position: "absolute", right: 8, top: 8 }}
              >
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent className="custom-scrollbar">
              <Grid>
                <img
                  src={selectedImage}
                  alt="issue"
                  style={{
                    height: "100%",
                    width: "100%",
                    objectFit: "cover",
                  }}
                />
              </Grid>
            </DialogContent>
          </Dialog>

          <Dialog
            open={openDialog}
            onClose={handleClose}
            maxWidth="lg"
            PaperProps={{
              sx: {
                width: "100%", // 80% of the viewport width
                height: "100%", // 80% of the viewport height
                padding: "20px",
              },
            }}
          >
            <Grid container item xs={12} alignContent={"flex-start"} rowGap={2}>
              <Grid container item xs={12} justifyContent={"flex-end"}>
                <CloseIcon
                  style={{ cursor: "pointer" }}
                  onClick={handleClose}
                />
              </Grid>

              <Grid container item xs={12} rowGap={3}>
                <Grid container item xs={12}>
                  <p style={{ fontWeight: "700" }}>
                    {" "}
                    Sl No :{" "}
                    <span style={{ fontWeight: "400" }}>
                      {showIssuedData?.slNo}
                    </span>{" "}
                  </p>
                </Grid>

                <Grid container item xs={12}>
                  <p style={{ fontWeight: "700" }}>
                    {" "}
                    Hall :{" "}
                    <span style={{ fontWeight: "400" }}>
                      {showIssuedData?.hall}
                    </span>{" "}
                  </p>
                </Grid>

                <Grid container item xs={12}>
                  <p style={{ fontWeight: "700" }}>
                    {" "}
                    Date :{" "}
                    <span style={{ fontWeight: "400" }}>
                      {formatReportedAt(showIssuedData)}
                    </span>{" "}
                  </p>
                </Grid>

                <Grid container item xs={12}>
                  <p style={{ fontWeight: "700" }}>
                    {" "}
                    Department :{" "}
                    <span style={{ fontWeight: "400" }}>
                      {showIssuedData?.department
                        ?.map((dept) => dept)
                        .join(", ")}
                    </span>{" "}
                  </p>
                </Grid>

                <Grid container item xs={12}>
                  <p style={{ fontWeight: "700" }}>
                    {" "}
                    Reporting Manager :{" "}
                    <span style={{ fontWeight: "400" }}>
                      {showIssuedData?.reportingManager
                        ?.map((manager) => manager?.name)
                        .join(", ")}
                    </span>{" "}
                  </p>
                </Grid>

                {showIssuedData?.employeeName && (
                  <Grid container item xs={12}>
                    <p style={{ fontWeight: "700" }}>
                      {" "}
                      Employee Name :{" "}
                      <span style={{ fontWeight: "400" }}>
                        {showIssuedData?.employeeName}
                      </span>{" "}
                    </p>
                  </Grid>
                )}

                {showIssuedData?.isEmergency && (
                  <Grid container item xs={12}>
                    <p style={{ fontWeight: "700" }}>
                      Emergency :{" "}
                      <span style={{ color: "#e03131" }}>YES</span>
                    </p>
                  </Grid>
                )}

                {showIssuedData?.machineName && (
                  <Grid container item xs={12}>
                    <p style={{ fontWeight: "700" }}>
                      Machine Name :{" "}
                      <span style={{ fontWeight: "400" }}>
                        {showIssuedData?.machineName}
                      </span>
                    </p>
                  </Grid>
                )}

                {showIssuedData?.reason && (
                  <Grid container item xs={12}>
                    <p style={{ fontWeight: "700" }}>
                      Reason :{" "}
                      <span style={{ fontWeight: "400" }}>
                        {showIssuedData?.reason}
                      </span>
                    </p>
                  </Grid>
                )}

                <Grid container item xs={12}>
                  <p style={{ fontWeight: "700", margin: 0, width: "100%" }}>
                    {" "}Description :{" "}
                  </p>
                  <p
                    style={{
                      fontWeight: "400",
                      color: "#e03131",
                      wordBreak: "break-word",
                      overflowWrap: "break-word",
                      whiteSpace: "pre-wrap",
                      width: "100%",
                      margin: "4px 0 0 0",
                    }}
                  >
                    {showIssuedData?.description}
                  </p>
                </Grid>

                <Grid container item xs={12}>
                  <p style={{ fontWeight: "700" }}>
                    {" "}
                    Status :{" "}
                    <span
                      style={{
                        fontWeight: "400",
                        ...statusStyle(showIssuedData?.status),
                      }}
                    >
                      {showIssuedData?.status}
                    </span>{" "}
                  </p>
                </Grid>

                <Grid container item xs={12}>
                  <p style={{ fontWeight: "700" }}>Solution : </p>
                </Grid>

                <Grid container item xs={12}>
                  <TableContainer component={Paper}>
                    <Table>
                      <TableHead>
                        <TableRow style={{ backgroundColor: "#f5f5f5" }}>
                          <TableCell style={{ fontWeight: "700" }}>
                            NAME
                          </TableCell>
                          <TableCell style={{ fontWeight: "700" }}>
                            SOLUTION
                          </TableCell>
                          <TableCell style={{ fontWeight: "700" }}>
                            SOLUTION DATE
                          </TableCell>
                          <TableCell style={{ fontWeight: "700" }}>
                            SEEKED DATE
                          </TableCell>
                          {hasSeekReason && (
                            <TableCell style={{ fontWeight: "700" }}>
                              SEEK REASON
                            </TableCell>
                          )}
                          <TableCell style={{ fontWeight: "700" }}>
                            IMAGE
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {showIssuedData?.reportingManagerSolution?.map(
                          (manager, index) => (
                            <TableRow key={index}>
                              <TableCell style={{ fontWeight: "400" }}>
                                {manager?.name}
                              </TableCell>
                              <TableCell
                                style={{ fontWeight: "400", maxWidth: "150px" }}
                              >
                                {manager?.solution ? (
                                  <p
                                    style={{
                                      textAlign: "justify",
                                      wordWrap: "break-word", // Breaks long words to fit within the container
                                      whiteSpace: "normal", // Allow wrapping on multiple lines
                                    }}
                                  >
                                    {manager?.solution}
                                  </p>
                                ) : (
                                  <p>NA</p>
                                )}
                              </TableCell>

                              <TableCell style={{ fontWeight: "400" }}>
                                {manager?.solutionDate ? (
                                  <p>
                                    {dayjs(manager?.solutionDate).format(
                                      "DD-MM-YYYY"
                                    )}
                                  </p>
                                ) : (
                                  <p>NA</p>
                                )}
                              </TableCell>
                              <TableCell style={{ fontWeight: "400" }}>
                                {manager?.seekedDate ? (
                                  <p>
                                    {dayjs(manager?.seekedDate).format(
                                      "DD-MM-YYYY"
                                    )}
                                  </p>
                                ) : (
                                  <p>NA</p>
                                )}
                              </TableCell>
                              {hasSeekReason && (
                                <TableCell
                                  style={{ fontWeight: "400", maxWidth: "150px" }}
                                >
                                  <p
                                    style={{
                                      wordWrap: "break-word",
                                      whiteSpace: "normal",
                                    }}
                                  >
                                    {manager?.seekReason || "NA"}
                                  </p>
                                </TableCell>
                              )}
                              <TableCell>
                                {solutionImages(manager).length ? (
                                  <div
                                    style={{
                                      display: "flex",
                                      flexWrap: "wrap",
                                      gap: "6px",
                                    }}
                                  >
                                    {solutionImages(manager).map((src, i) => (
                                      <img
                                        key={i}
                                        src={src}
                                        style={{
                                          width: "100px", // Adjusted to fit in the table cell
                                          height: "100px",
                                          objectFit: "cover",
                                        }}
                                        alt={`solution ${i + 1}`}
                                      />
                                    ))}
                                  </div>
                                ) : (
                                  "NA"
                                )}
                              </TableCell>
                            </TableRow>
                          )
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Grid>
              </Grid>
            </Grid>
          </Dialog>

          <Dialog
            open={openDialogERP}
            onClose={handleClose}
            maxWidth="lg"
            PaperProps={{
              sx: {
                width: "100%", // 80% of the viewport width
                height: "100%", // 80% of the viewport height
                padding: "20px",
              },
            }}
          >
            <Grid container item xs={12} alignContent={"flex-start"} rowGap={2}>
              <Grid container item xs={12} justifyContent={"flex-end"}>
                <CloseIcon
                  style={{ cursor: "pointer" }}
                  onClick={handleClose}
                />
              </Grid>

              <Grid container item xs={12} rowGap={3}>
                <Grid container item xs={12} md={4}>
                  <p style={{ fontWeight: "700" }}>
                    {" "}
                    Sl No :{" "}
                    <span style={{ fontWeight: "400" }}>
                      {showIssuedData?.slNo}
                    </span>{" "}
                  </p>
                </Grid>

                <Grid container item xs={12} md={4}>
                  <p style={{ fontWeight: "700" }}>
                    {" "}
                    Hall :{" "}
                    <span style={{ fontWeight: "400" }}>
                      {showIssuedData?.hall}
                    </span>{" "}
                  </p>
                </Grid>

                <Grid container item xs={12} md={4}>
                  <p style={{ fontWeight: "700" }}>
                    {" "}
                    Date :{" "}
                    <span style={{ fontWeight: "400" }}>
                      {formatReportedAt(showIssuedData)}
                    </span>{" "}
                  </p>
                </Grid>

                <Grid container item xs={12} md={4}>
                  <p
                    style={{
                      fontWeight: "700",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "wrap",
                    }}
                  >
                    {" "}
                    Department :{" "}
                    <span style={{ fontWeight: "400" }}>
                      {showIssuedData?.department
                        ?.map((dept) => dept)
                        .join(", ")}
                    </span>{" "}
                  </p>
                </Grid>

                <Grid container item xs={12} md={8}>
                  <p
                    style={{
                      fontWeight: "700",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "wrap",
                    }}
                  >
                    {" "}
                    Reporting Manager :{" "}
                    <span style={{ fontWeight: "400" }}>
                      {showIssuedData?.reportingManager
                        ?.map((manager) => manager?.name)
                        .join(", ")}
                    </span>{" "}
                  </p>
                </Grid>

                <Grid container item xs={12} md={4}>
                  <p style={{ fontWeight: "700" }}>
                    Status :{" "}
                    <span
                      style={{
                        fontWeight: "400",
                        ...statusStyle(showIssuedData?.status),
                      }}
                    >
                      {showIssuedData?.status}
                    </span>
                  </p>
                </Grid>

                <Grid container item xs={12} md={4}>
                  <p style={{ fontWeight: "700" }}>
                    Rejected Count :{" "}
                    <span
                      style={{
                        fontWeight: "400",
                        color:
                          showIssuedData?.rejectedCount > 0
                            ? STATUS_COLORS.REJECTED
                            : undefined,
                      }}
                    >
                      {rejectedCountText(showIssuedData)}
                    </span>
                  </p>
                </Grid>

                <Grid container item xs={12} md={4}>
                  <p style={{ fontWeight: "700" }}>
                    Last Rejected :{" "}
                    <span style={{ fontWeight: "400" }}>
                      {lastRejectedText(showIssuedData)}
                    </span>
                  </p>
                </Grid>

                {showIssuedData?.employeeName && (
                  <Grid container item xs={12}>
                    <p style={{ fontWeight: "700" }}>
                      {" "}
                      Employee Name :{" "}
                      <span style={{ fontWeight: "400" }}>
                        {showIssuedData?.employeeName}
                      </span>{" "}
                    </p>
                  </Grid>
                )}

                {showIssuedData?.isEmergency && (
                  <Grid container item xs={12}>
                    <p style={{ fontWeight: "700" }}>
                      Emergency :{" "}
                      <span style={{ color: "#e03131" }}>YES</span>
                    </p>
                  </Grid>
                )}

                {showIssuedData?.machineName && (
                  <Grid container item xs={12}>
                    <p style={{ fontWeight: "700" }}>
                      Machine Name :{" "}
                      <span style={{ fontWeight: "400" }}>
                        {showIssuedData?.machineName}
                      </span>
                    </p>
                  </Grid>
                )}

                {showIssuedData?.reason && (
                  <Grid container item xs={12}>
                    <p style={{ fontWeight: "700" }}>
                      Reason :{" "}
                      <span style={{ fontWeight: "400" }}>
                        {showIssuedData?.reason}
                      </span>
                    </p>
                  </Grid>
                )}

                <Grid container item xs={12} style={{ rowGap: "10px" }}>
                  <Grid item xs={12}>
                    <b>Description of issue :</b>
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      multiline
                      rows={4}
                      disabled
                      value={showIssuedData?.description}
                      variant="outlined"
                      fullWidth
                    />
                  </Grid>
                </Grid>

                {showIssuedData?.image?.length > 0 && (
                  <Grid
                    container
                    item
                    xs={12}
                    style={{ textAlign: "center", rowGap: "10px" }}
                  >
                    <Grid item xs={12}>
                      <b>Issue image</b>
                    </Grid>

                    <Grid
                      container
                      item
                      xs={12}
                      rowGap={5}
                      // spacing={2}
                      style={{
                        display: "flex",
                        flexWrap: "wrap",
                        // justifyContent: "space-between",
                        gap: "20px",
                      }}
                    >
                      {" "}
                      {showIssuedData?.image?.map((imgUrl, index) => {
                        return (
                          <Grid
                            item
                            xs={5.5}
                            md={3.8}
                            key={index}
                            style={{
                              // width: "200px",
                              display: "flex",
                              justifyContent: "center",
                            }}
                          >
                            <img
                              src={imgUrl}
                              alt="issue"
                              style={{
                                width: "100%",
                                height: "150px",
                                objectFit: "cover",
                                cursor: "pointer",
                              }}
                              onClick={() => handleClickOpen(imgUrl)}
                            />
                          </Grid>
                        );
                      })}
                    </Grid>
                  </Grid>
                )}

                {/* {showIssuedData?.video !== null && (
                  <Grid
                    container
                    item
                    xs={12}
                    style={{ textAlign: "center", rowGap: "10px" }}
                  >
                    <Grid item xs={12}>
                      <b>Issue video</b>
                    </Grid>

                    <video
                      src={showIssuedData?.video}
                      alt="Issued"
                      controls
                      style={{
                        height: "200px",
                        width: "200px",
                      }}
                    />
                  </Grid>
                )} */}

                <Grid container item xs={12}>
                  <hr
                    style={{
                      width: "100%",
                      border: "1px solid #ccc",
                    }}
                  />
                </Grid>

                <Grid container item xs={12}>
                  {showIssuedData?.reportingManagerSolution?.map(
                    (managerSolution, index) => {
                      return (
                        <Grid
                          container
                          item
                          xs={12}
                          key={index}
                          justifyContent={"space-between"}
                          style={{
                            rowGap: "20px",
                            padding: "20px 0",
                            borderBottom: "1px solid #ccc",
                          }}
                        >
                          <Grid
                            container
                            item
                            xs={7}
                            md={8}
                            style={{ rowGap: "10px" }}
                          >
                            <Grid item xs={12}>
                              <b>{`Solution ${index + 1}: ${
                                managerSolution?.name
                              }`}</b>
                            </Grid>

                            <Grid item xs={12}>
                              <TextField
                                multiline
                                rows={4}
                                disabled
                                value={managerSolution?.solution}
                                variant="outlined"
                                fullWidth
                              />
                            </Grid>

                            {managerSolution?.seekedDate && (
                              <Grid item xs={12}>
                                <b>Seeked Date :</b>{" "}
                                {dayjs(managerSolution.seekedDate).format(
                                  "DD-MM-YYYY"
                                )}
                              </Grid>
                            )}
                            {managerSolution?.seekReason && (
                              <Grid
                                item
                                xs={12}
                                style={{
                                  wordBreak: "break-word",
                                  whiteSpace: "pre-wrap",
                                }}
                              >
                                <b>Seek Reason :</b>{" "}
                                {managerSolution.seekReason}
                              </Grid>
                            )}
                          </Grid>

                          <Grid
                            container
                            item
                            xs={4}
                            md={3}
                            style={{ textAlign: "center", rowGap: "10px" }}
                          >
                            <Grid item xs={12}>
                              <b>
                                Image
                                {solutionImages(managerSolution).length > 1 &&
                                  `s (${solutionImages(managerSolution).length})`}
                              </b>
                            </Grid>

                            <Grid item xs={12}>
                              {solutionImages(managerSolution).length > 0 ? (
                                <Grid
                                  item
                                  xs={12}
                                  style={{
                                    display: "flex",
                                    flexWrap: "wrap",
                                    gap: "8px",
                                    justifyContent: "center",
                                  }}
                                >
                                  {solutionImages(managerSolution).map(
                                    (src, i) => (
                                      <img
                                        key={i}
                                        src={src}
                                        alt={`solution ${i + 1}`}
                                        style={{
                                          width: "200px",
                                          height: "150px",
                                          objectFit: "cover",
                                          cursor: "pointer",
                                        }}
                                        onClick={() => handleClickOpen(src)}
                                      />
                                    )
                                  )}
                                </Grid>
                              ) : (
                                <p>No image preview</p>
                              )}
                            </Grid>
                          </Grid>

                          <Grid
                            container
                            item
                            xs={12}
                            justifyContent="flex-end"
                            spacing={2}
                          >
                            {managerSolution?.accepted === null &&
                            (managerSolution?.solution !== null ||
                              managerSolution?.image !== null) ? (
                              !showIssuedData?.approvableManagerIds?.includes(
                                Number(managerSolution?.id)
                              ) ? (
                                // Another superior approves this manager
                                <b style={{ color: "#f08c00" }}>
                                  AWAITING APPROVAL
                                </b>
                              ) : (
                              <>
                                <Grid item>
                                  <Button
                                    variant="contained"
                                    color="primary"
                                    style={{
                                      backgroundColor: "#2f9e44",
                                      color: "white",
                                      textTransform: "none",
                                    }}
                                    onClick={() =>
                                      openReview("accepted", managerSolution)
                                    }
                                    disabled={isERPmutateLoading}
                                  >
                                    Accept
                                  </Button>
                                </Grid>
                                <Grid item>
                                  <Button
                                    variant="contained"
                                    color="secondary"
                                    style={{
                                      backgroundColor: "#e03131",
                                      color: "white",
                                      textTransform: "none",
                                    }}
                                    onClick={() =>
                                      openReview("decline", managerSolution)
                                    }
                                    disabled={isERPmutateLoading}
                                  >
                                    Decline
                                  </Button>
                                </Grid>
                              </>
                              )
                            ) : managerSolution.accepted === true ? (
                              <b style={{ color: "#2f9e44" }}>ACCEPTED</b>
                            ) : (
                              managerSolution.accepted === false && (
                                <b style={{ color: "#e03131" }}>DECLINED</b>
                              )
                            )}
                          </Grid>

                          {/* Why the current solution was declined */}
                          {managerSolution?.accepted === false &&
                            lastDeclineOf(managerSolution) && (
                              <Grid
                                item
                                xs={12}
                                style={{
                                  borderLeft: `4px solid ${STATUS_COLORS.REJECTED}`,
                                  borderRadius: "6px",
                                  backgroundColor: "#fff1f1",
                                  padding: "10px 14px",
                                  wordBreak: "break-word",
                                  whiteSpace: "pre-wrap",
                                }}
                              >
                                <div
                                  style={{
                                    color: STATUS_COLORS.REJECTED,
                                    fontWeight: 700,
                                    fontSize: "13px",
                                    textTransform: "uppercase",
                                    letterSpacing: "0.04em",
                                  }}
                                >
                                  Last Rejected Reason
                                </div>
                                <div
                                  style={{
                                    fontSize: "17px",
                                    fontWeight: 600,
                                    margin: "4px 0",
                                  }}
                                >
                                  {lastDeclineOf(managerSolution).remarks}
                                </div>
                                <div style={{ color: "#6b7280" }}>
                                  {dayjs(
                                    lastDeclineOf(managerSolution).date
                                  ).format("DD-MM-YYYY, hh:mm A")}
                                  {lastDeclineOf(managerSolution).by
                                    ? ` · ${lastDeclineOf(managerSolution).by}`
                                    : ""}
                                </div>
                              </Grid>
                            )}

                          {managerSolution?.reviews?.length > 0 && (
                            <Grid
                              item
                              xs={12}
                              style={{
                                wordBreak: "break-word",
                                whiteSpace: "pre-wrap",
                              }}
                            >
                              <b>Review Comments :</b>
                              {managerSolution.reviews.map((review, i) => (
                                <div
                                  key={i}
                                  style={
                                    i === managerSolution.reviews.length - 1
                                      ? { fontWeight: 600 }
                                      : undefined
                                  }
                                >
                                  <span
                                    style={{
                                      color:
                                        review.decision === "accept"
                                          ? "#2f9e44"
                                          : "#e03131",
                                      fontWeight: 600,
                                    }}
                                  >
                                    {review.decision === "accept"
                                      ? "Accepted"
                                      : "Declined"}
                                  </span>{" "}
                                  ({dayjs(review.date).format(
                                    "DD-MM-YYYY, hh:mm A"
                                  )}
                                  {review.by ? `, ${review.by}` : ""}):{" "}
                                  {review.remarks}
                                  {i === managerSolution.reviews.length - 1 &&
                                    managerSolution.reviews.length > 1 && (
                                      <span style={{ color: "#6b7280" }}>
                                        {" "}
                                        (latest)
                                      </span>
                                    )}
                                </div>
                              ))}
                            </Grid>
                          )}

                          {/* Earlier solutions declined and replaced */}
                          {managerSolution?.history?.length > 0 && (
                            <Grid
                              container
                              item
                              xs={12}
                              rowGap={1.5}
                              style={{
                                border: `1px solid ${STATUS_COLORS.REJECTED}`,
                                backgroundColor: "#fff5f5",
                                borderRadius: "6px",
                                padding: "12px 16px",
                              }}
                            >
                              <Grid item xs={12}>
                                <b style={{ color: STATUS_COLORS.REJECTED }}>
                                  Previously Rejected Solutions (
                                  {managerSolution.history.length})
                                </b>
                              </Grid>
                              {managerSolution.history.map((attempt, i) => (
                                <Grid
                                  item
                                  xs={12}
                                  key={i}
                                  style={{
                                    wordBreak: "break-word",
                                    whiteSpace: "pre-wrap",
                                    paddingTop: i ? "10px" : 0,
                                    borderTop: i ? "1px dashed #f1aeb5" : "none",
                                  }}
                                >
                                  <div>
                                    <b>{`#${i + 1}`}</b>
                                    {attempt.solutionDate &&
                                      ` (${dayjs(attempt.solutionDate).format(
                                        "DD-MM-YYYY, hh:mm A"
                                      )})`}
                                    : {attempt.solution || "NA"}
                                  </div>
                                  {attempt.review && (
                                    <div>
                                      <b>Reason :</b> {attempt.review.remarks}{" "}
                                      ({dayjs(attempt.review.date).format(
                                        "DD-MM-YYYY, hh:mm A"
                                      )}
                                      {attempt.review.by
                                        ? `, ${attempt.review.by}`
                                        : ""}
                                      )
                                    </div>
                                  )}
                                  {attempt.images?.length > 0 && (
                                    <div
                                      style={{
                                        display: "flex",
                                        flexWrap: "wrap",
                                        gap: "6px",
                                        marginTop: "6px",
                                      }}
                                    >
                                      {attempt.images.map((src, j) => (
                                        <img
                                          key={j}
                                          src={src}
                                          alt={`rejected solution ${j + 1}`}
                                          style={{
                                            width: "80px",
                                            height: "80px",
                                            objectFit: "cover",
                                            cursor: "pointer",
                                          }}
                                          onClick={() => handleClickOpen(src)}
                                        />
                                      ))}
                                    </div>
                                  )}
                                </Grid>
                              ))}
                            </Grid>
                          )}
                        </Grid>
                      );
                    }
                  )}
                </Grid>
              </Grid>
            </Grid>
          </Dialog>
        </Grid>
      )}
    </Grid>
  );
}

export default TaskSheet;
